import { useInfiniteQuery, useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query'

import { receivablesTableFields } from '@/modules/receivables/enums/receivablesEnums'
import { In } from '@hello-aria/query-string'
import { ComparisonFilterInput, QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { isEmpty, isNil } from 'lodash'
import { Debtor } from '../models/debtorModels'
import { Invoice, UpdateAttachments } from '../models/invoiceModels'
import { QueryParams } from '../models/queryModels'
import { CreateReceivable, Receivable, UpdateReceivable } from '../models/receivableModels'
import { getDebtorsService } from '../services/debtorServices'
import { getInvoicesService } from '../services/invoicesServices'
import {
    createReceivableService,
    getReceivableByIdService,
    listReceivablesService,
    updateReceivableService
} from '../services/receivableServices'
import { RequestResponse } from '../services/requestServices'
import { getPageSize, mapQuery } from '../utils/queryUtils'
import { useMe } from './useMe'
import { useQueryParamsState } from './useQueryParams'
import { State, updateAttachmentsService } from '../services/uploadServices'

export const receivablesQueryKeys = {
    root: ['receivables'],
    deprecated_infiniteReceivables: (
        companyId: string,
        queryParams?: QueryParams
    ): (string | QueryParams | undefined)[] => [...receivablesQueryKeys.root, companyId, queryParams],
    infiniteReceivables: (companyId: string, query?: QueryStringInput): (string | QueryStringInput | undefined)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        query
    ],
    receivableById: (companyId: string, receivableId: string | undefined): (undefined | string)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        receivableId
    ]
}

type UseInfiniteReceivables = {
    isEnabled?: boolean
    query?: QueryStringInput
}

export const useInfiniteReceivables = (options?: UseInfiniteReceivables) => {
    const { companyId } = useMe()
    const { isEnabled, query } = options ?? {}

    return useInfiniteQuery({
        queryKey: receivablesQueryKeys.infiniteReceivables(companyId, query),

        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            return listReceivablesService({
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },
        enabled: isEnabled ?? true,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}
interface deprecated_UseInfiniteReceivables {
    isEnabled?: boolean
    customFilters?: ComparisonFilterInput[]
}

export const deprecated_useInfiniteReceivables = (options?: deprecated_UseInfiniteReceivables) => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()
    const filters = [...(options?.customFilters ?? []), ...(queryParamsState.get('receivables_filter') ?? [])]

    return useInfiniteQuery({
        queryKey: receivablesQueryKeys.deprecated_infiniteReceivables(
            companyId,
            queryParamsState.pick([`receivables_search`, `receivables_sort`, `receivables_filter`])
        ),
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            let relatedInvoices: RequestResponse<Invoice[]> | undefined = undefined
            let relatedDebtors: RequestResponse<Debtor[]> | undefined = undefined
            const receivables = await listReceivablesService({
                companyId,
                query: mapQuery({
                    filter: !isEmpty(filters)
                        ? {
                              type: 'logical',
                              operator: 'and',
                              filters
                          }
                        : undefined,
                    search: { value: queryParamsState.get('receivables_search'), path: receivablesTableFields },
                    sortBy: queryParamsState.get('receivables_sort'),
                    pagination: { after: pageParam }
                })
            })

            const relatedInvoiceFilters = In(
                'id',
                receivables?.body?.map((receivable) => receivable.invoiceId).filter(Boolean) as string[]
            )

            const relatedDebtorFilters = In(
                'identifier.value',
                receivables?.body?.map((receivable) => receivable.debtorIdentifier.value).filter(Boolean) as string[]
            )

            if (relatedInvoiceFilters && !isEmpty(relatedInvoiceFilters.value)) {
                relatedInvoices = await getInvoicesService({
                    companyId,
                    hasAttachments: false,
                    query: mapQuery({
                        filter: relatedInvoiceFilters
                    })
                })
            }
            if (relatedDebtorFilters && !isEmpty(relatedDebtorFilters.value)) {
                relatedDebtors = await getDebtorsService({
                    companyId,
                    query: mapQuery({
                        filter: relatedDebtorFilters
                    })
                })
            }

            const body = receivables?.body?.map((receivable) => {
                const invoice = relatedInvoices?.body?.find((invoice) => invoice.id === receivable.invoiceId)
                const debtor = relatedDebtors?.body?.find((debtor) => debtor.id === receivable.debtorId)

                return {
                    ...receivable,
                    invoice,
                    debtor
                }
            })

            return {
                ...receivables,
                body
            }
        },
        enabled: options?.isEnabled ?? true,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}

export const useReceivableById = (receivableId: string | undefined) => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: receivablesQueryKeys.receivableById(companyId, receivableId),
        queryFn: () => getReceivableByIdService(companyId, receivableId),
        enabled: !isNil(receivableId)
    })
}

export const useCreateReceivable = (): UseMutationResult<
    RequestResponse<Receivable> | undefined,
    unknown,
    CreateReceivable
> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => createReceivableService(companyId, payload)
    })
}

export const useUpdateReceivable = (): UseMutationResult<
    RequestResponse<Receivable> | undefined,
    unknown,
    UpdateReceivable
> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => updateReceivableService(companyId, payload)
    })
}

export const useUpdateReceivableAttachments = (
    state: State
): UseMutationResult<RequestResponse<Receivable | undefined> | undefined, unknown, UpdateAttachments<Receivable>> => {
    const company = useMe()
    return useMutation({
        mutationFn: (payload) => updateAttachmentsService('receivables', company.companyId, payload, state)
    })
}

export const useInvalidateReceivables = () => {
    const { companyId } = useMe()
    const queryClient = useQueryClient()

    const invalidateReceivables = () => {
        queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.root })
    }

    const invalidateReceivableById = (receivableId: string) => {
        queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.receivableById(companyId, receivableId) })
    }

    return {
        invalidateReceivables,
        invalidateReceivableById
    }
}
